import { AuthBaseClient } from '../base-client';
import type { SearchResponse } from '@vagusx/shared';
import type {
  ProviderResponse,
  CreateProviderRequest,
  UpdateProviderRequest,
  AssignLocationRequest,
  ProviderLocationResponse,
  ProviderLocationWithDetails,
} from './types';

/**
 * Client for provider API endpoints.
 */
export class ProviderClient extends AuthBaseClient {
  /**
   * List all active providers.
   */
  async list(): Promise<SearchResponse<ProviderResponse>> {
    return this.get<SearchResponse<ProviderResponse>>('/api/providers');
  }

  /**
   * List inactive (deactivated) providers.
   */
  async listInactive(): Promise<SearchResponse<ProviderResponse>> {
    return this.get<SearchResponse<ProviderResponse>>('/api/providers/inactive');
  }

  /**
   * Get provider by ID.
   */
  async getById(id: string): Promise<ProviderResponse> {
    return this.get<ProviderResponse>(`/api/providers/${id}`);
  }

  /**
   * Create a new provider.
   */
  async create(data: CreateProviderRequest): Promise<ProviderResponse> {
    return this.post<ProviderResponse>('/api/providers', data);
  }

  /**
   * Update a provider.
   */
  async update(id: string, data: UpdateProviderRequest): Promise<ProviderResponse> {
    return this.patch<ProviderResponse>(`/api/providers/${id}`, data);
  }

  /**
   * Delete a provider (soft-delete — sets row_status = INACTIVE).
   */
  async deleteProvider(id: string): Promise<void> {
    return this.delete<void>(`/api/providers/${id}`);
  }

  /**
   * Reactivate an inactive provider.
   */
  async reactivate(id: string): Promise<ProviderResponse> {
    return this.post<ProviderResponse>(`/api/providers/${id}/reactivate`, {});
  }

  /**
   * Assign a location to a provider.
   */
  async assignLocation(
    providerId: string,
    data: AssignLocationRequest
  ): Promise<ProviderLocationResponse> {
    return this.post<ProviderLocationResponse>(`/api/providers/${providerId}/locations`, data);
  }

  /**
   * Remove a location from a provider.
   */
  async removeLocation(providerId: string, locationId: string): Promise<void> {
    return this.delete<void>(`/api/providers/${providerId}/locations/${locationId}`);
  }

  /**
   * Set a location as primary for a provider.
   */
  async setPrimaryLocation(
    providerId: string,
    locationId: string
  ): Promise<ProviderLocationResponse> {
    return this.put<ProviderLocationResponse>(
      `/api/providers/${providerId}/locations/${locationId}/primary`
    );
  }

  /**
   * Get all locations for a provider.
   */
  async getLocations(providerId: string): Promise<ProviderLocationWithDetails[]> {
    return this.get<ProviderLocationWithDetails[]>(`/api/providers/${providerId}/locations`);
  }
}
